import { SearchClient } from 'instantsearch.js/es/types'
import { PlainSearchParameters } from 'algoliasearch-helper'

import { DebtorService } from '@app-services/debtor/debtor.service'
import { resolveSearchBackend } from '@app-domains/meilisearch/clients/index-router'
import { mapContentInstantSearchRequest } from '@app-domains/meilisearch/mappers/content-search-request.mapper'
import { mapContentSearchResult } from '@app-domains/meilisearch/mappers/content-search-response.mapper'
import { mapDealerInstantSearchRequest } from '@app-domains/meilisearch/mappers/dealer-search-request.mapper'
import { mapDealerSearchResult } from '@app-domains/meilisearch/mappers/dealer-search-response.mapper'
import { mapInstantSearchRequest } from '@app-domains/meilisearch/mappers/search-request.mapper'
import { mapProductSearchResult } from '@app-domains/meilisearch/mappers/search-response.mapper'
import { SearchRequest } from '@app-domains/meilisearch/types/search-client.types'
import { ContentSearchGatewayService } from '@app-domains/meilisearch/services/content-search-gateway.service'
import { DealerSearchGatewayService } from '@app-domains/meilisearch/services/dealer-search-gateway.service'
import { ProductSearchGatewayService } from '@app-domains/meilisearch/services/product-search-gateway.service'

export interface SearchClientDeps {
    productGateway: ProductSearchGatewayService
    contentGateway: ContentSearchGatewayService
    dealerGateway: DealerSearchGatewayService
    debtorService: DebtorService
}

export function createSearchClient(deps: SearchClientDeps): SearchClient {
    const { productGateway, contentGateway, dealerGateway, debtorService } = deps

    async function searchSingle(request: SearchRequest) {
        const params = (request.params ?? {}) as PlainSearchParameters
        const backend = resolveSearchBackend(request.indexName)

        if (backend === 'product') {
            const input = mapInstantSearchRequest(
                request.indexName,
                params,
                debtorService.isNL.getValue(),
            )
            const result = await productGateway.search(input)
            return mapProductSearchResult(result, request.indexName, input.query ?? '')
        }

        if (backend === 'content') {
            const input = mapContentInstantSearchRequest(request.indexName, params)
            const result = await contentGateway.search(input)
            return mapContentSearchResult(result, request.indexName, input.query ?? '')
        }

        const input = mapDealerInstantSearchRequest(params)
        const result = await dealerGateway.search(input)
        return mapDealerSearchResult(result, request.indexName, input.query ?? '')
    }

    return {
        search(requests: readonly SearchRequest[]) {
            return Promise.all([...requests].map(searchSingle)).then((results) => ({ results }))
        },
    } as SearchClient
}
